import { type HTMLAttributes, type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface AvatarTooltipProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  accessibleLabel?: string;
  children: ReactNode;
  disabled?: boolean;
  name: string;
}

export function AvatarTooltip({
  accessibleLabel,
  children,
  disabled = false,
  name,
  ...props
}: AvatarTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [standaloneFocusable, setStandaloneFocusable] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [ancestorFocused, setAncestorFocused] = useState(false);
  const [ancestorTooltipName, setAncestorTooltipName] = useState(name);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    if (disabled || !trigger || trigger.closest('[aria-hidden="true"], [inert]')) {
      setStandaloneFocusable(false);
      setAncestorFocused(false);
      setAncestorTooltipName(name);
      return;
    }

    const interactiveAncestor = trigger.closest<HTMLElement>("a[href], button");
    if (!interactiveAncestor) {
      setStandaloneFocusable(true);
      setAncestorFocused(false);
      setAncestorTooltipName(name);
      return;
    }

    setStandaloneFocusable(false);
    const avatarTriggers = Array.from(
      interactiveAncestor.querySelectorAll<HTMLElement>('[data-avatar-tooltip-trigger="true"]'),
    );
    if (avatarTriggers[0] !== trigger) {
      setAncestorFocused(false);
      setAncestorTooltipName(name);
      return;
    }

    const names = avatarTriggers
      .map((avatar) => avatar.dataset.avatarTooltipName)
      .filter((avatarName): avatarName is string => Boolean(avatarName));
    setAncestorTooltipName(names.join(", ") || name);

    const onFocusIn = () => setAncestorFocused(true);
    const onFocusOut = () => setAncestorFocused(false);
    interactiveAncestor.addEventListener("focusin", onFocusIn);
    interactiveAncestor.addEventListener("focusout", onFocusOut);
    setAncestorFocused(interactiveAncestor.contains(document.activeElement));

    return () => {
      interactiveAncestor.removeEventListener("focusin", onFocusIn);
      interactiveAncestor.removeEventListener("focusout", onFocusOut);
    };
  });

  if (disabled) {
    return (
      <span {...props} aria-label={accessibleLabel ?? name} role="img">
        {children}
      </span>
    );
  }

  return (
    <Tooltip onOpenChange={setTriggerOpen} open={triggerOpen || ancestorFocused}>
      <TooltipTrigger asChild>
        <span
          {...props}
          aria-label={accessibleLabel ?? name}
          data-avatar-tooltip-name={name}
          data-avatar-tooltip-trigger="true"
          ref={triggerRef}
          role="img"
          tabIndex={standaloneFocusable ? 0 : undefined}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{ancestorFocused ? ancestorTooltipName : name}</TooltipContent>
    </Tooltip>
  );
}
